<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu 
      :user="user" 
      :isMenuOpen="isMenuOpen" 
      @toggle-menu="toggleMenu" 
      @close-menu="closeMenu" 
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold mb-6">Registro de Prospectos</h1>
          
          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
          
          <div class="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            <p v-if="adviserName">
              Si tienes dudas o comentarios, no dudes en contactarte con tu asesor, {{ adviserName }}.
            </p>
            <p v-else>
              Si tienes dudas o comentarios, no dudes en contactarte con tu asesor.
            </p>
          </div>
          
          <div v-if="successMessage" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {{ successMessage }}
          </div>
          
          <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ errorMessage }}
          </div>

          <!-- Alerta CURP duplicado / reingreso -->
          <div v-if="curpAlertMessage" class="bg-orange-100 border border-orange-400 text-orange-800 px-4 py-3 rounded mb-4 font-semibold">
            {{ curpAlertMessage }}
          </div>
          
          <form @submit.prevent="submitForm" class="mt-6">
            <!-- Datos personales -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
              <h2 class="text-lg font-semibold mb-4">Datos personales</h2>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

                <!-- CURP primero + botón Buscar -->
                <div class="md:col-span-2">
                  <label for="curp" class="block text-sm font-medium text-gray-500">CURP*</label>
                  <div class="mt-1 flex flex-col sm:flex-row gap-2">
                    <input 
                      id="curp" 
                      v-model="formData.Curp"
                      @input="onCurpInput"
                      type="text" 
                      maxlength="18"
                      required
                      :disabled="curpLocked"
                      class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 uppercase disabled:bg-gray-100 disabled:text-gray-500"
                      placeholder="Ej: ABCD123456ABCDEF01"
                    />
                    <button
                      type="button"
                      @click="buscarCURP"
                      :disabled="!curpFormatoValido || curpLocked || loadingCurp"
                      class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      <svg v-if="loadingCurp" class="animate-spin h-4 w-4 mr-2 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                      {{ loadingCurp ? 'Buscando...' : 'Buscar' }}
                    </button>
                  </div>
                  <p v-if="curpError" class="mt-1 text-sm text-red-600">{{ curpError }}</p>
                  <p v-if="curpLocked" class="mt-1 text-sm text-green-600">✓ CURP validado correctamente</p>
                </div>

                <!-- Nombre -->
                <div>
                  <label for="nombre" class="block text-sm font-medium text-gray-500">Nombre*</label>
                  <input 
                    id="nombre" 
                    v-model="formData.Names" 
                    type="text" 
                    required
                    :disabled="curpDatosLlenados"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
                
                <!-- Apellido Paterno -->
                <div>
                  <label for="apellidoPaterno" class="block text-sm font-medium text-gray-500">Apellido Paterno*</label>
                  <input 
                    id="apellidoPaterno" 
                    v-model="formData.FatherLastName" 
                    type="text"
                    :required="!soloUnApellido"
                    :disabled="curpDatosLlenados"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
                
                <!-- Apellido Materno -->
                <div>
                  <label for="apellidoMaterno" class="block text-sm font-medium text-gray-500">Apellido Materno</label>
                  <input 
                    id="apellidoMaterno" 
                    v-model="formData.MotherLastName" 
                    type="text"
                    :disabled="curpDatosLlenados"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>

                <!-- Sexo (deshabilitado si vino del CURP) -->
                <div>
                  <label class="block text-sm font-medium text-gray-500">Sexo*</label>
                  <div class="mt-2 space-x-6 flex">
                    <div class="flex items-center">
                      <input 
                        id="sexoMasculino" 
                        v-model="formData.Sex" 
                        value="M" 
                        type="radio" 
                        required
                        :disabled="curpDatosLlenados"
                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 disabled:opacity-50"
                      />
                      <label for="sexoMasculino" class="ml-2 block text-sm text-gray-700">Masculino</label>
                    </div>
                    <div class="flex items-center">
                      <input 
                        id="sexoFemenino" 
                        v-model="formData.Sex" 
                        value="F" 
                        type="radio"
                        :disabled="curpDatosLlenados"
                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 disabled:opacity-50"
                      />
                      <label for="sexoFemenino" class="ml-2 block text-sm text-gray-700">Femenino</label>
                    </div>
                  </div>
                </div>

                <!-- Fecha de Nacimiento -->
                <div>
                  <label for="fechaNacimiento" class="block text-sm font-medium text-gray-500">Fecha de Nacimiento*</label>
                  <input 
                    id="fechaNacimiento" 
                    v-model="formData.Birthdate" 
                    type="date" 
                    required
                    :disabled="curpDatosLlenados"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>

                <!-- Teléfono Móvil -->
                <div>
                  <label for="telefonoMovil" class="block text-sm font-medium text-gray-500">Teléfono Móvil*</label>
                  <input 
                    id="telefonoMovil" 
                    v-model="formData.Mobile" 
                    type="tel" 
                    maxlength="10"
                    pattern="[0-9]{10}"
                    required
                    @input="validatePhoneInput('Mobile')"
                    @blur="validatePhone('Mobile')"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    :class="{ 'border-red-500': phoneErrors.Mobile }"
                  />
                  <p v-if="phoneErrors.Mobile" class="mt-1 text-sm text-red-600">{{ phoneErrors.Mobile }}</p>
                </div>

                <!-- Email -->
                <div>
                  <label for="email" class="block text-sm font-medium text-gray-500">Email</label>
                  <input 
                    id="email" 
                    v-model="formData.Email" 
                    type="email"
                    @blur="validateEmail"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    :class="{ 'border-red-500': emailError }"
                  />
                  <p v-if="emailError" class="mt-1 text-sm text-red-600">{{ emailError }}</p>
                </div>

                <!-- Campos ocultos -->
                <div class="hidden">
                  <input type="hidden" v-model="formData.Phone" />
                  <input type="hidden" v-model="formData.IsCurpValid" />
                  <input type="hidden" v-model="formData.Entidad" />
                  <input type="hidden" v-model="formData.Acta" />
                  <input type="hidden" v-model="formData.Municipio" />
                  <input type="hidden" v-model="formData.Registro" />
                </div>

                <div class="hidden">
                  <input type="hidden" v-model="formData.WorkId" />
                  <input type="hidden" v-model="formData.SchoolId" />
                </div>
              </div>
            </div>
            
            <!-- Lugar de origen (oculto) -->
            <div class="hidden">
              <input type="hidden" v-model="formData.OriginCountryId" />
              <input type="hidden" v-model="formData.OriginStateId" />
              <input type="hidden" v-model="formData.OriginCityId" />
            </div>
            
            <!-- Domicilio actual (OCULTO - se envían valores por defecto) -->
            <div class="hidden">
              <input type="hidden" v-model="formData.ActualCountryId" />
              <input type="hidden" v-model="formData.ActualStateId" />
              <input type="hidden" v-model="formData.ActualCityId" />
              <input type="hidden" v-model="formData.ActualColonyId" />
              <input type="hidden" v-model="formData.Street" />
            </div>
            
            <!-- Información académica -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
              <h2 class="text-lg font-semibold mb-4">Información académica</h2>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="instituto" class="block text-sm font-medium text-gray-500">Instituto*</label>
                  <select 
                    id="instituto" 
                    v-model="formData.InstituteId" 
                    required
                    @change="loadCampus"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar instituto</option>
                    <option v-for="instituto in institutos" :key="instituto.InstituteId" :value="instituto.InstituteId">{{ instituto.InstituteName.toUpperCase() }}</option>
                  </select>
                </div>

                <div class="hidden">
                  <select v-model="formData.InstituteProperty">
                    <option value="Propio" selected>Propio</option>
                  </select>
                </div>
                
                <div>
                  <label for="campus" class="block text-sm font-medium text-gray-500">Campus*</label>
                  <select 
                    id="campus" 
                    v-model="formData.CampusId" 
                    required
                    @change="loadPeriodos"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar campus</option>
                    <option v-for="campus in campuses" :key="campus.CampusId" :value="campus.CampusId">{{ campus.CampusName }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="periodo" class="block text-sm font-medium text-gray-500">Periodo*</label>
                  <select 
                    id="periodo" 
                    v-model="periodoSeleccionado" 
                    required
                    @change="loadCarreras"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar periodo</option>
                    <option v-for="periodo in periodos" :key="periodo.PeriodUda" :value="periodo.PeriodUda">{{ periodo.Description.toUpperCase() }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="carrera" class="block text-sm font-medium text-gray-500">Carrera*</label>
                  <select 
                    id="carrera" 
                    v-model="formData.CareerId" 
                    required
                    @change="loadTurnos"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar carrera</option>
                    <option v-for="carrera in carreras" :key="carrera.CareerId" :value="carrera.CareerId">{{ carrera.CareerName }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="turno" class="block text-sm font-medium text-gray-500">Turno*</label>
                  <select 
                    id="turno" 
                    v-model="formData.ShiftId" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar turno</option>
                    <option v-for="turno in turnos" :key="turno.ShiftId" :value="turno.ShiftId">{{ turno.ShiftName.toUpperCase() }}</option>
                  </select>
                </div>
              </div>
            </div>
            
            <!-- Información adicional -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
              <h2 class="text-lg font-semibold mb-4">Información adicional</h2>
              
              <div class="grid grid-cols-1 gap-6">
                <div class="hidden">
                  <input type="hidden" v-model="formData.ActivityId" />
                </div>
                
                <div>
                  <label for="comentarios" class="block text-sm font-medium text-gray-500">Comentarios</label>
                  <textarea 
                    id="comentarios" 
                    v-model="formData.Comments" 
                    rows="4" 
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Información relevante sobre el prospecto..."
                  ></textarea>
                </div>
              </div>
            </div>
            
            <!-- Botones de acción -->
            <div class="flex flex-col sm:flex-row gap-3 justify-end">
              <button 
                type="button"
                class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                @click="resetForm"
              >
                <svg class="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v6h6M20 20v-6h-6M4 14a8 8 0 0114-5.3M20 10a8 8 0 01-14 5.3" />
                </svg>
                Limpiar formulario
              </button>
              <button 
                type="submit"
                :disabled="loading || !!curpAlertMessage"
                class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="!loading" class="w-4 h-4 mr-2 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7" />
                </svg>
                {{ loading ? 'Enviando...' : 'Registrar prospecto' }}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import {
  getSchools,
  getWorks,
  getCountries,
  /*getStates,
  getCities,
  getColonies,*/
  getInstitutesByStudent,
  getCampus,
  getProspectPeriods,
  getCareers,
  getShifts,
  createProspect,
  createProspectPeriod,
  getStudentData,
  getCURP
} from '@/services/api.js';

// Regex oficial CURP 18 caracteres
const CURP_REGEX = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/i;

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      loading: false,
      loadingData: false,
      loadingCurp: false,
      successMessage: null,
      errorMessage: null,
      curpError: null,
      curpAlertMessage: null,   // Mensaje de bloqueo cuando CURP ya existe
      curpLocked: false,         // Bloquea edición del CURP cuando fue validado exitosamente
      curpDatosLlenados: false,  //Bloquea edición de campos personales llenados por CURP
      soloUnApellido: false,     // Permite guardar con un solo apellido
      trabajaActualmente: false,
      periodoSeleccionado: '',
      adviserName: '',
      phoneErrors: {
        Phone: null,
        Mobile: null
      },
      emailError: null,
      // Arrays
      escuelas: [],
      trabajos: [],
      paises: [],
      estadosOrigen: [],
      ciudadesOrigen: [],
      estadosActual: [],
      ciudadesActual: [],
      colonias: [],
      institutos: [],
      campuses: [],
      periodos: [],
      carreras: [],
      turnos: [],
      formData: {
        InstituteProperty: "Propio",
        ActivityId: 0,
        Names: "",
        FatherLastName: "",
        MotherLastName: "",
        Curp: "",
        Email: "",
        Birthdate: "",
        Sex: "",
        Phone: "",
        Mobile: "",
        SchoolId: 4520,
        WorkId: "",
        OriginCountryId: "",
        OriginStateId: "",
        OriginCityId: "",
        // Domicilio actual — siempre se envían en 0 / vacío
        ActualCountryId: 0,
        ActualStateId: 0,
        ActualCityId: 0,
        ActualColonyId: 0,
        Street: "",
        InstituteId: "",
        CampusId: "",
        CareerId: "",
        ShiftId: "",
        Comments: "",
        UserName: "",
        // Campos ocultos CURP
        IsCurpValid: 0,
        Entidad: 0,
        Acta: 0,
        Municipio: 0,
        Registro: 0
      }
    };
  },

  computed: {
    curpFormatoValido() {
      return CURP_REGEX.test(this.formData.Curp);
    }
  },

  async created() {
    await this.getUserFromBackend();
    if (this.user) {
      this.formData.UserName = this.user.username || "";
      await this.loadAdviserName();
      await this.loadInitialData();
    }
  },

  methods: {

    onCurpInput() {
      // Forzar mayúsculas
      this.formData.Curp = this.formData.Curp.toUpperCase();
      // Si el usuario edita el CURP, limpiar estado previo
      this.curpError = null;
      this.curpAlertMessage = null;
      this.curpDatosLlenados = false;
      // Limpiar campos personales
      this.formData.Names = "";
      this.formData.FatherLastName = "";
      this.formData.MotherLastName = "";
      this.formData.Sex = "";
      this.formData.Birthdate = "";
      this.soloUnApellido = false;
      // Limpiar campos ocultos CURP
      this.formData.IsCurpValid = 0;
      this.formData.Entidad = 0;
      this.formData.Acta = 0;
      this.formData.Municipio = 0;
      this.formData.Registro = 0;
    },

    async buscarCURP() {
      if (!this.curpFormatoValido || this.curpLocked) return;

      this.loadingCurp = true;
      this.curpError = null;
      this.curpAlertMessage = null;

      try {
        const response = await getCURP({ curp: this.formData.Curp });
        const data = response.data;

        // ── Caso: CURP válido, YA EXISTE en BD (code_error = 101 o similar con datos de prospecto) ──
        if (data.error === false && typeof data.prospectid !== 'undefined') {
          // Llenar datos visibles
          this.formData.Names = data.Names || "";
          this.formData.FatherLastName = data.FatherLastName || "";
          this.formData.MotherLastName = data.MotherLastName || "";
          this.formData.Sex = data.Sex || "";
          this.formData.Birthdate = data.Birthdate || "";
          this.curpDatosLlenados = true;

          // Variables ocultas para prospecto existente
          this.formData.IsCurpValid = 1;
          this.formData.Entidad = 0;
          this.formData.Acta = 0;
          this.formData.Municipio = 0;
          this.formData.Registro = 0;

          // Mostrar mensaje de bloqueo si error_message tiene contenido
          if (data.error_message && data.error_message.trim() !== '') {
            this.curpAlertMessage = data.error_message;
          }
          // Si error_message está vacío, puede continuar (no bloqueamos)

          return;
        }

        // ── Caso: CURP válido, NO existe en BD (code_error = 0, tiene Solicitante) ──
        if (data.error === false && data.code_error === 0 && data.response && data.response.Solicitante) {
          const sol = data.response.Solicitante;
          const doc = data.response.DocProbatorio;

          // Mapear género H→M, M→F
          let gen = "";
          if (sol.ClaveSexo === 'H') { gen = 'M'; }
          if (sol.ClaveSexo === 'M') { gen = 'F'; }

          this.formData.Names = sol.Nombres || "";
          this.formData.FatherLastName = sol.ApellidoPaterno || "";
          this.formData.MotherLastName = sol.ApellidoMaterno || "";
          this.formData.Sex = gen;
          this.formData.Birthdate = sol.FechaNacimiento || "";

          // Detectar si solo viene un apellido
          this.soloUnApellido = !sol.ApellidoMaterno || sol.ApellidoMaterno.trim() === '';

          // Campos ocultos
          this.formData.IsCurpValid = 1;
          this.formData.Entidad = doc?.NumEntidadRegistrante ?? 0;
          this.formData.Acta = doc?.NumActa ?? 0;
          this.formData.Municipio = doc?.ClaveMunicipioRegistro ?? 0;
          this.formData.Registro = doc?.AnioRegistro ?? 0;

          // Bloquear campos personales y CURP
          this.curpDatosLlenados = true;
          this.curpLocked = true;

          return;
        }

        // ── Caso: error en la respuesta ──
        this.curpError = data.error_message || "No se pudo validar el CURP. Intente nuevamente.";

      } catch (error) {
        console.error("Error al consultar CURP:", error);
        this.curpError = "Error al conectar con el servicio de CURP. Por favor intente nuevamente.";
      } finally {
        this.loadingCurp = false;
      }
    },


    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.user.username = this.user.email.match(/^a(\d+)@/)[1];
        } else {
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },

    async loadAdviserName() {
      try {
        const response = await getStudentData(this.user.username);
        if (response.data[0] && response.data[0].Adviser) {
          this.adviserName = response.data[0].Adviser;
        }
      } catch (error) {
        console.error("Error cargando nombre del asesor:", error);
      }
    },

    async loadInitialData() {
      this.loadingData = true;
      try {
        await Promise.all([
          this.loadEscuelas(),
          this.loadTrabajos(),
          this.loadPaises(),
          this.loadInstitutos()
        ]);
      } catch (error) {
        console.error("Error cargando datos iniciales:", error);
        await this.$alert('Error cargando datos del formulario', 'Por favor, intente nuevamente.', 'error');
      } finally {
        this.loadingData = false;
      }
    },

    async loadEscuelas() {
      try {
        const response = await getSchools();
        this.escuelas = response.data || [];
      } catch (error) { console.error("Error cargando escuelas:", error); throw error; }
    },

    async loadTrabajos() {
      try {
        const response = await getWorks();
        this.trabajos = response.data || [];
      } catch (error) { console.error("Error cargando trabajos:", error); throw error; }
    },

    async loadPaises() {
      try {
        const response = await getCountries();
        this.paises = response.data || [];
      } catch (error) { console.error("Error cargando países:", error); throw error; }
    },

    async loadInstitutos() {
      try {
        const response = await getInstitutesByStudent(this.user.username);
        this.institutos = response.data || [];
      } catch (error) { console.error("Error cargando institutos:", error); throw error; }
    },

    async loadCampus() {
      if (!this.formData.InstituteId) return;
      try {
        const response = await getCampus(this.formData.InstituteId);
        this.campuses = response.data || [];
        this.formData.CampusId = "";
        this.periodoSeleccionado = "";
        this.formData.CareerId = "";
        this.formData.ShiftId = "";
      } catch (error) { console.error("Error cargando campus:", error); }
    },

    async loadPeriodos() {
      if (!this.formData.CampusId) return;
      try {
        const response = await getProspectPeriods(this.formData.CampusId);
        this.periodos = response.data || [];
        this.periodoSeleccionado = "";
        this.formData.CareerId = "";
        this.formData.ShiftId = "";
      } catch (error) { console.error("Error cargando periodos:", error); }
    },

    async loadCarreras() {
      if (!this.periodoSeleccionado || !this.formData.CampusId) return;
      try {
        const response = await getCareers(this.formData.CampusId, this.periodoSeleccionado);
        this.carreras = response.data || [];
        this.formData.CareerId = "";
        this.formData.ShiftId = "";
      } catch (error) { console.error("Error cargando carreras:", error); }
    },

    async loadTurnos() {
      if (!this.formData.CareerId || !this.periodoSeleccionado || !this.formData.CampusId) return;
      try {
        const response = await getShifts(this.formData.CampusId, this.periodoSeleccionado, this.formData.CareerId);
        this.turnos = response.data || [];
        this.formData.ShiftId = "";
      } catch (error) { console.error("Error cargando turnos:", error); }
    },


    validatePhoneInput(field) {
      this.formData[field] = this.formData[field].replace(/[^0-9]/g, '');
      if (this.formData[field].length > 10) {
        this.formData[field] = this.formData[field].slice(0, 10);
      }
      if (this.phoneErrors[field]) {
        this.phoneErrors[field] = null;
      }
    },

    validatePhone(field) {
      const phone = this.formData[field];
      if (!phone && field === 'Mobile') {
        this.phoneErrors[field] = 'El teléfono móvil es obligatorio';
        return false;
      }
      if (phone && phone.length !== 10) {
        this.phoneErrors[field] = 'Por favor, ingrese un teléfono de 10 dígitos.';
        return false;
      }
      if (phone && !/^\d{10}$/.test(phone)) {
        this.phoneErrors[field] = 'El teléfono debe contener solo números';
        return false;
      }
      this.phoneErrors[field] = null;
      return true;
    },

    validateEmail() {
      const email = this.formData.Email;
      if (!email) { this.emailError = null; return true; }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.emailError = 'Por favor, ingrese un email válido.';
        return false;
      }
      this.emailError = null;
      return true;
    },

    validateForm() {
      let isValid = true;
      if (!this.validatePhone('Mobile')) isValid = false;
      if (this.formData.Phone && !this.validatePhone('Phone')) isValid = false;
      if (!this.validateEmail()) isValid = false;
      return isValid;
    },


    async submitForm() {
      this.successMessage = null;
      this.errorMessage = null;

      // Bloquear si hay mensaje de alerta CURP
      if (this.curpAlertMessage) {
        await this.$alert('No es posible registrar este prospecto', this.curpAlertMessage, 'warning');
        return;
      }

      try {
        this.loading = true;

        if (!this.validateForm()) {
          throw new Error("Por favor corrija los errores en el formulario");
        }

        if (!this.formData.Names) {
          throw new Error("Debe completar el nombre del prospecto");
        }
        if (!this.formData.FatherLastName && !this.soloUnApellido) {
          throw new Error("Debe completar el apellido paterno");
        }

        // Asegurar valores por defecto de domicilio (siempre 0 / vacío)
        this.formData.ActualCountryId = 0;
        this.formData.ActualStateId = 0;
        this.formData.ActualCityId = 0;
        this.formData.ActualColonyId = 0;
        this.formData.Street = "";

        // Campos ocultos que deben ir vacíos
        this.formData.Phone = "";
        this.formData.WorkId = "";
        this.formData.OriginCountryId = "";
        this.formData.OriginStateId = "";
        this.formData.OriginCityId = "";

        const prospectoResponse = await createProspect(this.formData);
        const prospectId = prospectoResponse.data.Message;

        if (!prospectId) {
          throw new Error("No se pudo obtener el ID del prospecto");
        }

        if (this.periodoSeleccionado) {
          await createProspectPeriod({
            PeriodId: this.periodoSeleccionado,
            ProspectId: prospectId
          });
        }

        this.$nextTick(() => {
          this.successMessage = 'El prospecto ha sido registrado exitosamente.';
        });

        await this.$alert('Prospecto registrado exitosamente', '', 'success');
        this.resetForm();

      } catch (error) {
        console.error('Error al registrar prospecto:', error);
        this.$nextTick(() => {
          this.errorMessage = error.response?.data?.message || error.message || 'Ocurrió un error al registrar el prospecto. Por favor intente nuevamente.';
        });
        await this.$alert('Error al registrar el prospecto', 'Por favor, intente nuevamente.', 'error');
      } finally {
        this.loading = false;
      }
    },


    resetForm() {
      this.formData = {
        InstituteProperty: "Propio",
        ActivityId: 0,
        Names: "",
        FatherLastName: "",
        MotherLastName: "",
        Curp: "",
        Email: "",
        Birthdate: "",
        Sex: "",
        Phone: "",
        Mobile: "",
        SchoolId: 4520,
        WorkId: "",
        OriginCountryId: "",
        OriginStateId: "",
        OriginCityId: "",
        ActualCountryId: 0,
        ActualStateId: 0,
        ActualCityId: 0,
        ActualColonyId: 0,
        Street: "",
        InstituteId: "",
        CampusId: "",
        CareerId: "",
        ShiftId: "",
        Comments: "",
        UserName: this.user?.username || "",
        IsCurpValid: 0,
        Entidad: 0,
        Acta: 0,
        Municipio: 0,
        Registro: 0
      };
      this.trabajaActualmente = false;
      this.periodoSeleccionado = "";
      this.curpLocked = false;
      this.curpDatosLlenados = false;
      this.curpError = null;
      this.curpAlertMessage = null;
      this.soloUnApellido = false;
      this.phoneErrors = { Phone: null, Mobile: null };
      this.emailError = null;
      this.successMessage = null;
      this.errorMessage = null;
    },

    // Menú/Sesión

    toggleMenu() { this.isMenuOpen = !this.isMenuOpen; },
    closeMenu() { this.isMenuOpen = false; },

    async logout() {
      try {
        await axios.get('/sanctum/csrf-cookie');
        await new Promise(resolve => setTimeout(resolve, 100));
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
        });
        console.log(response);
        document.cookie.split(";").forEach(function(c) {
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>